import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Dimensions, Modal, Platform } from 'react-native';
import styled from 'styled-components/native';

import { Feather } from '@expo/vector-icons';

import accessibilityTags from '../../../accessibilityTags';

import Colors from '../../constants/Colors';
import ListItem from '../list/ListItem';
import ListItemIcon from '../list/ListItemIcon';
import ListItemText from '../list/ListItemText';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';
import { FetchError } from '../../api/api';
import { UploadFile, useUploadFiles } from '../../hooks/upload/useUploadFiles';
import ActivityIndicatorScreen from '../ActivityIndicatorScreen';

const StyledBackdrop = styled.View`
  height: 100%;
  width: 100%;
  background: #00000066;
`;

const StyledBackdropTransparentTouchable = styled.TouchableOpacity`
  flex: 1 1 auto;
  width: 100%;
`;

const StyledActionSheetView = styled(Animated.View)`
  background: #fff;
`;

interface ImageActionSheetProps {
  visible: boolean;
  /**
   * Specify uploadPath to auto upload to S3 when image is selected.
   */
  uploadPath?: string;
  /**
   * Specify array of image resize constraints (pixels). onImage will return an
   * array of resized images.
   */
  resize?: number[];
  presignBody?: any | any[];
  selectText?: string;
  captureText?: string;
  pickerOptions?: ImagePicker.ImagePickerOptions;
  disableCameraCapture?: boolean;
  onSelectImage?: (image: ImagePicker.ImagePickerResult) => void;
  onResizeImages?: (images: ImageManipulator.ImageResult[]) => void;
  onResizeImageFiles?: (files: File[]) => void;
  onUploadedImages?: (uploadedFiles: (UploadFile | undefined)[]) => void;
  onUploadVideos?: (uploadedFiles: (UploadFile | undefined)[]) => void;
  onUploading?: (uploading: boolean) => void;
  onUploadError?: (error: FetchError) => void;
  onClose: () => void;
}

const ImageActionSheet: React.FC<ImageActionSheetProps> = ({
  visible,
  uploadPath = '',
  resize,
  presignBody = { mimeType: 'image/jpeg' },
  selectText = 'Take a new image/video',
  captureText = 'Choose an existing image/video',
  pickerOptions = {
    mediaTypes: ImagePicker.MediaTypeOptions.All,
    allowsEditing: true,
    aspect: [1, 1],
    quality: 1
  },
  disableCameraCapture,
  onSelectImage,
  onResizeImages,
  onResizeImageFiles,
  onUploadedImages,
  onUploadVideos,
  onUploading,
  onUploadError,
  onClose
}) => {
  if ((onResizeImages || onResizeImageFiles) && !resize) {
    throw new Error('No resize props provided.');
  }

  const onUploadingRef = useRef(onUploading);
  onUploadingRef.current = onUploading;
  const onUploadErrorRef = useRef(onUploadError);
  onUploadErrorRef.current = onUploadError;
  const [resizing, setResizing] = useState<boolean>();

  // handle uploading if S3 uploadPath is specified
  const { upload, error, uploading } = useUploadFiles(uploadPath);

  useEffect(() => {
    onUploadingRef.current?.(uploading);
  }, [uploading]);

  useEffect(() => {
    if (error) onUploadErrorRef.current?.(error);
  }, [error]);

  const getFileTypeFromMime = (mimeType: string) => {
    switch (mimeType) {
      case 'image/jpeg':
        return { fileType: 'jpg', mimeType, type: 'image' as const };
      case 'image/png':
        return { fileType: 'png', mimeType, type: 'image' as const };
      case 'image/heic':
        return { fileType: 'heic', mimeType, type: 'image' as const };
      case 'image/gif':
        return { fileType: 'gif', mimeType, type: 'image' as const };
      case 'video/mp4':
        return { fileType: 'mp4', mimeType, type: 'video' as const };
      case 'video/x-m4v':
        return { fileType: 'm4v', mimeType, type: 'video' as const };
      case 'video/quicktime':
        return { fileType: 'mp4', mimeType: 'video/mp4', type: 'video' as const };
    }
    return undefined;
  };

  const getFileType = (uri: string, mimeType?: string | null) => {
    let fileType = '';
    if (Platform.OS !== 'web') {
      const arr = uri.split('.');
      if (arr) {
        fileType = arr[arr.length - 1].toLowerCase();
      }
    } else if (uri.startsWith('data:')) {
      const webMimeType = uri.split(':')[1].split(';')[0];
      return getFileTypeFromMime(webMimeType);
    } else if (mimeType) {
      return getFileTypeFromMime(mimeType);
    }
    if (fileType) {
      switch (fileType) {
        case 'jpeg':
        case 'jpg':
          return { fileType, mimeType: 'image/jpeg', type: 'image' };
        case 'png':
          return { fileType, mimeType: 'image/png', type: 'image' };
        case 'heic':
          return { fileType, mimeType: 'image/heic', type: 'image' };
        case 'gif':
          return { fileType, mimeType: 'image/gif', type: 'image' };
        case 'mp4':
          return { fileType, mimeType: 'video/mp4', type: 'video' };
        case 'm4v':
          return { fileType, mimeType: 'video/x-m4v', type: 'video' };
        case 'mov':
          return { fileType: 'mp4', mimeType: 'video/mp4', type: 'video' };
      }
    }
    return mimeType ? getFileTypeFromMime(mimeType) : undefined;
  };

  const getAssetMediaType = (asset: ImagePicker.ImagePickerAsset) =>
    getFileType(asset.uri, asset.mimeType)?.type ?? asset.type;

  const handleImagePicker = useCallback(
    async (result: ImagePicker.ImagePickerResult) => {
      if (result.canceled) return;
      setResizing(true);
      onSelectImage?.(result);
      const images: ImageManipulator.ImageResult[] = [];
      const videos: {
        uri: string;
        base64?: string;
        mimeType?: string;
        file?: File;
      }[] = [];
      // resize images if resize array is provided
      if (resize) {
        for (const size of resize) {
          for (const asset of result.assets) {
            const type = getAssetMediaType(asset);
            if (type === 'image') {
              images.push(await resizeImage(asset, size));
            } else if (type === 'video') {
              videos.push({
                uri: asset.uri,
                base64: asset.base64 || undefined,
                mimeType: asset.mimeType,
                file: asset.file
              });
            }
          }
        }
        onResizeImages?.(images);
      } else {
        for (const asset of result.assets) {
          if (getAssetMediaType(asset) === 'image') {
            images.push({
              uri: asset.uri,
              width: asset.width,
              height: asset.height,
              base64: asset.base64 || undefined
            });
          } else if (getAssetMediaType(asset) === 'video') {
            videos.push({
              uri: asset.uri,
              base64: asset.base64 || undefined,
              mimeType: asset.mimeType,
              file: asset.file
            });
          }
        }
      }

      // skip if not returning resized image files and not uploading
      if (!onResizeImageFiles && !uploadPath) {
        setResizing(false);
        return;
      }

      // create resized image files
      const imageFiles = await Promise.all(
        images.map((image, index) => {
          const type = getFileType(image.uri);
          return getFileFromUri(
            image.uri,
            `image${resize?.[index] ? `-${resize[index]}` : ''}.${
              type?.fileType ? type.fileType : 'jpg'
            }`,
            type?.mimeType || 'image/jpeg'
          );
        })
      );
      const videoFiles = await Promise.all(
        videos.map(video => {
          if (Platform.OS === 'web' && video.file) {
            return video.file;
          }
          const type = getFileType(video.uri, video.mimeType);
          return getFileFromUri(
            video.uri,
            `video.${type?.fileType ? type.fileType : 'mp4'}`,
            type?.mimeType || 'video/mp4'
          );
        })
      );

      onResizeImageFiles?.(imageFiles);
      setResizing(false);

      // upload files if uploadPath is provided
      if (uploadPath) {
        if (imageFiles) {
          const uploadedImages = await Promise.all(
            imageFiles.map((imageFile, index) =>
              upload(imageFile, {
                presignBody: Array.isArray(presignBody)
                  ? presignBody[index]
                  : presignBody
              })
            )
          );
          onUploadedImages?.(uploadedImages);
        }
        if (videoFiles) {
          const uploadedVideos = await Promise.all(
            videoFiles.map(videoFile => upload(videoFile))
          );
          onUploadVideos?.(uploadedVideos);
        }
      }
    },
    [
      onResizeImageFiles,
      onResizeImages,
      onSelectImage,
      onUploadVideos,
      onUploadedImages,
      presignBody,
      resize,
      upload,
      uploadPath
    ]
  );

  // Animate slide up from bottom
  const { height } = Dimensions.get('window');
  const animated = useRef(new Animated.Value(height)).current;
  useEffect(() => {
    Animated.timing(animated, {
      toValue: visible ? 0 : height,
      duration: 300,
      useNativeDriver: Platform.OS === 'ios' || Platform.OS === 'android'
    }).start();
  }, [animated, visible, height]);

  const handleCameraAction = useCallback(
    async (
      mediaTypes: ImagePicker.MediaTypeOptions = ImagePicker.MediaTypeOptions
        .All
    ) => {
      if (Platform.OS !== 'web') {
        // check if permission granted
        const { status } = await ImagePicker.getCameraPermissionsAsync();
        if (status !== 'granted') {
          // if no, ask for permission (first time only)
          const { status } = await ImagePicker.requestCameraPermissionsAsync();
          if (status !== 'granted') {
            // if permission denied, can't access camera
            alert('Sorry, we need camera permissions to make this work!');
            return;
          }
        }
      }
      const result = await ImagePicker.launchCameraAsync({
        ...pickerOptions,
        mediaTypes
      });
      await handleImagePicker(result);
    },
    [handleImagePicker, pickerOptions]
  );

  return (
    <Modal
      animationType='fade'
      hardwareAccelerated
      visible={visible}
      transparent
    >
      <StyledBackdrop>
        <StyledBackdropTransparentTouchable onPress={() => onClose()} />
        {!uploading && !resizing ? (
          <StyledActionSheetView
            style={{ transform: [{ translateY: animated }] }}
          >
            {!disableCameraCapture &&
              (Platform.OS === 'android' ? (
                // Provide separate buttons for either image or video capture as Android's provided camera capture intents doesn't support both image and video at the same time.
                <>
                  <ListItem
                    onPress={() =>
                      handleCameraAction(ImagePicker.MediaTypeOptions.Images)
                    }
                    accessibilityLabel={accessibilityTags.takeImageID}
                    testID={accessibilityTags.takeImageID}
                  >
                    <ListItemIcon>
                      <Feather
                        name='camera'
                        size={29}
                        color={Colors.primaryPurple}
                      />
                    </ListItemIcon>
                    <ListItemText primary={'Take a new image'} />
                  </ListItem>
                  <ListItem
                    onPress={() =>
                      handleCameraAction(ImagePicker.MediaTypeOptions.Videos)
                    }
                  >
                    <ListItemIcon>
                      <Feather
                        name='video'
                        size={29}
                        color={Colors.primaryPurple}
                      />
                    </ListItemIcon>
                    <ListItemText primary={'Take a new video'} />
                  </ListItem>
                </>
              ) : (
                <ListItem onPress={() => handleCameraAction()}>
                  <ListItemIcon>
                    <Feather
                      name='camera'
                      size={29}
                      color={Colors.primaryPurple}
                    />
                  </ListItemIcon>
                  <ListItemText primary={selectText} />
                </ListItem>
              ))}
            <ListItem
              onPress={async () => {
                if (Platform.OS !== 'web') {
                  // check if permission granted
                  const { status } =
                    await ImagePicker.getMediaLibraryPermissionsAsync();
                  if (status !== 'granted') {
                    // if no, ask for permission (first time only)
                    const { status } =
                      await ImagePicker.requestMediaLibraryPermissionsAsync();
                    if (status !== 'granted') {
                      // if permission denied, can't access media
                      alert(
                        'Sorry, we need media library permissions to make this work!'
                      );
                      return;
                    }
                  }
                }
                const result = await ImagePicker.launchImageLibraryAsync(
                  pickerOptions
                );
                await handleImagePicker(result);
              }}
            >
              <ListItemIcon>
                <Feather name='image' size={29} color={Colors.primaryPurple} />
              </ListItemIcon>
              <ListItemText primary={captureText} />
            </ListItem>
            <ListItem onPress={() => onClose()}>
              <ListItemIcon>
                <Feather name='x' size={29} color={Colors.primaryPurple} />
              </ListItemIcon>
              <ListItemText primary='Cancel' />
            </ListItem>
          </StyledActionSheetView>
        ) : (
          <ActivityIndicatorScreen modal={true} color={Colors.primary} />
        )}
      </StyledBackdrop>
    </Modal>
  );
};

export default ImageActionSheet;

export const getFileFromUri = async (
  uri: string,
  filename: string,
  mimeType: string
) => {
  const response = await fetch(uri);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
};

const resizeImage = async (
  image: ImagePicker.ImagePickerAsset,
  size: number
) => {
  const dimension = image.width > image.height ? 'width' : 'height';
  return ImageManipulator.manipulateAsync(
    image.uri,
    [{ resize: { [dimension]: size } }],
    { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG, base64: true }
  );
};
